import * as m from '@staiter/i18n';
import * as tx from '@staiter/i18n';
/**
 * FM-S-multi-4-11 — /admin/holdings list page (super-admin only).
 *
 * Features:
 * - List holdings + total_entities_count
 * - CreateHoldingDialog (FM-4-15)
 * - Link to detail page (FM-4-12)
 */
import { useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Building2, Plus } from 'lucide-react';
import { useState } from 'react';
import { PageHeader, Table } from '~/components/data';
import { emitToast } from '~/components/feedback';
import { Button, Dialog, Input } from '~/components/primitives';
import { useCreateHolding, useHoldings } from '~/lib/api/tenancy';
import { formatDate } from '~/lib/format';
import { useIsSuperAdmin } from '~/lib/rbac/use-tenancy-rbac';

export const Route = createFileRoute('/_auth/admin/holdings')({ component: HoldingsAdmin });

function HoldingsAdmin(): JSX.Element {
  const isSuper = useIsSuperAdmin();
  const { data, isLoading } = useHoldings();
  const [createOpen, setCreateOpen] = useState(false);

  if (!isSuper) {
    return (
      <div style={{ padding: '24px 0' }}>
        <p style={{ color: 'var(--text-muted)' }}>
          {tx.ui_upravlenie_holdingami_dostupno_tolko_super_admin_svyazhit()}
        </p>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={m.admin_holdings_title()}
        description={m.admin_holdings_subtitle()}
        actions={
          <Button onClick={() => setCreateOpen(true)} leadingIcon={<Plus size={14} />}>
            {tx.ui_sozdat_holding()}
          </Button>
        }
      />

      {isLoading ? (
        <p>{tx.ui_zagruzka()}</p>
      ) : (
        <Table
          ariaLabel={m.admin_holdings_title()}
          data={data?.items ?? []}
          rowKey={(h) => h.id}
          columns={[
            {
              id: 'name',
              header: m.admin_col_name(),
              accessor: (h) => (
                <Link
                  to="/admin/holdings/$holdingId"
                  params={{ holdingId: h.id }}
                  style={{ color: 'var(--text-link)', textDecoration: 'none' }}
                >
                  <Building2 size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                  {h.name}
                </Link>
              ),
            },
            {
              id: 'entities',
              header: m.admin_holdings_col_companies(),
              accessor: (h) => h.total_entities_count ?? 0,
              align: 'right',
            },
            { id: 'status', header: m.admin_col_status(), accessor: (h) => h.status },
            {
              id: 'created',
              header: m.admin_col_created(),
              accessor: (h) => formatDate(h.created_at),
            },
          ]}
          emptyTitle={m.admin_holdings_empty()}
          emptyDescription={m.admin_holdings_empty_hint()}
        />
      )}

      {createOpen && <CreateHoldingDialog onClose={() => setCreateOpen(false)} />}
    </div>
  );
}

/** FM-S-multi-4-15 — CreateHoldingDialog. */
function CreateHoldingDialog({ onClose }: { onClose: () => void }): JSX.Element {
  const [name, setName] = useState('');
  const qc = useQueryClient();
  const createMut = useCreateHolding();

  return (
    <Dialog open onClose={onClose} title={tx.ui_sozdat_holding()}>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (!name.trim()) return;
          try {
            await createMut.mutateAsync({ name: name.trim() });
            emitToast({ variant: 'success', title: m.admin_holdings_created() });
            await qc.invalidateQueries({ queryKey: ['admin', 'holdings'] });
            onClose();
          } catch (err) {
            emitToast({ variant: 'error', title: m.admin_holdings_create_failed() });
            void err;
          }
        }}
      >
        <Input
          label={m.admin_col_name()}
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          autoFocus
        />
        <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button type="button" variant="ghost" onClick={onClose}>
            {tx.ui_otmena()}
          </Button>
          <Button type="submit" disabled={createMut.isPending}>
            {tx.ui_sozdat()}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
